import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Twitter, Youtube, Globe, Mail, Phone, ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { useSiteContent } from '@/context/SiteContentContext';
import { SEOHead } from '@/components/seo/SEOHead';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { usePageDesign } from '@/hooks/usePageDesign';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const TeamPage: React.FC = () => {
  const { template, seoCanonical } = useSiteContent();
  const brandName = template.brand?.name || 'Our Company';
  const [activeDept, setActiveDept] = useState<string>('all');
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('team');

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['public-team-members'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('team_members')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const departments = useMemo(() => {
    const depts = new Set<string>();
    teamMembers.forEach(m => { if (m.department) depts.add(m.department); });
    return Array.from(depts).sort();
  }, [teamMembers]);

  const filteredMembers = useMemo(() => {
    if (activeDept === 'all') return teamMembers;
    return teamMembers.filter(m => m.department === activeDept);
  }, [teamMembers, activeDept]);

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const socialIcons = [
    { key: 'facebook_url', icon: Facebook },
    { key: 'instagram_url', icon: Instagram },
    { key: 'linkedin_url', icon: Linkedin },
    { key: 'twitter_url', icon: Twitter },
    { key: 'youtube_url', icon: Youtube },
    { key: 'website_url', icon: Globe },
  ] as const;

  return (
    <>
      <SEOHead
        title={`Our Team | ${brandName}`}
        description={`Meet the experienced professionals at ${brandName}. Our dedicated team brings years of expertise and a commitment to quality service.`}
        canonical={seoCanonical ? `${seoCanonical}/team` : undefined}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Meet Our Team"
          subtitle="The experienced professionals behind our success. Get to know the people who make it all happen."
          badge="Our People"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Team' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
          showRating
          rating={template.stats?.googleRating || 4.9}
          reviewCount={template.stats?.reviewCount || 100}
        />

        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            {/* Department Filter */}
            {departments.length > 1 && (
              <div className="flex flex-wrap justify-center gap-2 mb-12">
                <Button
                  variant={activeDept === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveDept('all')}
                >
                  All ({teamMembers.length})
                </Button>
                {departments.map(dept => (
                  <Button
                    key={dept}
                    variant={activeDept === dept ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveDept(dept)}
                  >
                    {dept} ({teamMembers.filter(m => m.department === dept).length})
                  </Button>
                ))}
              </div>
            )}

            {filteredMembers.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">Team information coming soon.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {filteredMembers.map((member, index) => (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className="bg-card p-6 rounded-xl border border-border hover:shadow-lg transition-all hover:border-primary/20 group"
                  >
                    <div className="text-center">
                      <Avatar className="w-28 h-28 mx-auto mb-4 ring-4 ring-primary/10 group-hover:ring-primary/20 transition-all">
                        <AvatarImage src={member.featured_image || ''} alt={member.featured_image_alt || member.full_name} />
                        <AvatarFallback className="bg-primary/10 text-primary font-semibold text-2xl">
                          {getInitials(member.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="font-heading font-semibold text-xl">{member.full_name}</h3>
                      {member.title && <p className="text-primary text-sm font-medium mb-1">{member.title}</p>}
                      {member.department && (
                        <span className="inline-block text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full mb-3">
                          {member.department}
                        </span>
                      )}
                      {member.description && (
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{member.description}</p>
                      )}
                    </div>

                    {/* Contact & Social */}
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex flex-col gap-1.5 mb-3">
                        {member.email && (
                          <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                            <Mail className="h-3.5 w-3.5" />
                            {member.email}
                          </a>
                        )}
                        {member.phone && (
                          <a href={`tel:${member.phone.replace(/\D/g, '')}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                            <Phone className="h-3.5 w-3.5" />
                            {member.phone}
                          </a>
                        )}
                      </div>
                      <div className="flex justify-center gap-3">
                        {socialIcons.map(({ key, icon: Icon }) => {
                          const url = (member as any)[key];
                          if (!url) return null;
                          return (
                            <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                              <Icon className="h-4 w-4" />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="font-heading text-3xl font-bold mb-4">Ready to Work With Us?</h2>
            <p className="text-muted-foreground mb-8">Get in touch with our team today.</p>
            <Button asChild size="lg">
              <Link to="/contact">
                Contact Us <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default TeamPage;
